import { MusicAPIIntegrationID } from '../music-services/ids';

/**
 * The music services the Catalog screen shows a connection card for.
 *
 * Every service with an artist programme in the connectors focus list
 * (`docs/music24-connectors/PLAN.md`). It is deliberately shorter than every
 * integration musicapi knows: a card for a service with nothing to connect to is
 * a dead end. Services we have not built yet offer the early-access waiting list
 * (`artistPortalEarlyAccessServices` in `artistPortals.ts`).
 *
 * Shared so the server, which synthesizes a row per service, and the screen,
 * which draws a card per service, cannot drift apart.
 */
export const catalogConnectionServices: string[] = [
  MusicAPIIntegrationID.Spotify,
  MusicAPIIntegrationID.AppleMusic,
  MusicAPIIntegrationID.YoutubeMusic,
  MusicAPIIntegrationID.Tidal,
  MusicAPIIntegrationID.Deezer,
  MusicAPIIntegrationID.AmazonMusic,
  MusicAPIIntegrationID.Boomplay,
  MusicAPIIntegrationID.SoundCloud,
  MusicAPIIntegrationID.Audiomack,
];

/**
 * The distributors an artist can connect. Not music services: they deliver the
 * audio to the stores and report the money. Their ids are plain strings in the
 * same `integrationType` column as the music services.
 *
 * A distributor has no public artist profile, so it takes no profile url and
 * earns no bio-link block. It only offers the early-access waiting list.
 */
export enum DistributorID {
  DistroKid = 'distrokid',
  TuneCore = 'tunecore',
  CDBaby = 'cdbaby',
  Ditto = 'ditto',
  UnitedMasters = 'unitedmasters',
  ONErpm = 'onerpm',
  TuneCoreJapan = 'tunecorejapan',
}

/** The Catalog screen's distributor section, in the order it draws them. */
export const catalogDistributorServices: string[] = [
  DistributorID.DistroKid,
  DistributorID.TuneCore,
  DistributorID.CDBaby,
  DistributorID.Ditto,
  DistributorID.UnitedMasters,
  DistributorID.ONErpm,
  DistributorID.TuneCoreJapan,
];

/** Display names. Brand names, so they are not translated. */
export const distributorNames: Record<string, string> = {
  [DistributorID.DistroKid]: 'DistroKid',
  [DistributorID.TuneCore]: 'TuneCore',
  [DistributorID.CDBaby]: 'CD Baby',
  [DistributorID.Ditto]: 'Ditto Music',
  [DistributorID.UnitedMasters]: 'UnitedMasters',
  [DistributorID.ONErpm]: 'ONErpm',
  [DistributorID.TuneCoreJapan]: 'TuneCore Japan',
};

/**
 * Every connection card, music services first, then distributors. This is the
 * deck `GET /accounts/:accountId/serviceConnections` answers.
 */
export const catalogAllConnectionServices: string[] = [...catalogConnectionServices, ...catalogDistributorServices];

/**
 * How a catalog row got there.
 *
 * Keep in sync with `AccountCatalogItemKind` in `server/prisma/main/schema.prisma`.
 */
export enum CatalogItemKind {
  claimed = 'claimed',
  unreleased = 'unreleased',
}

/**
 * Whose release it is. `featured` is promotable, but it is labelled everywhere
 * so it never reads as the artist's own single.
 *
 * Keep in sync with `AccountCatalogItemOwnership` in `server/prisma/main/schema.prisma`.
 */
export enum CatalogItemOwnership {
  mine = 'mine',
  featured = 'featured',
}

/**
 * Where the account stands with one service's artist programme.
 *
 * `connected` and `expired` are produced by the artist-portal providers (see
 * `artistPortals.ts`). `requested` on an early-access service means "on the
 * waiting list" (`artistPortalEarlyAccessServices`). The old stub connect flow
 * still produces `requested` and `pending` for a built service, and only in
 * development.
 *
 * Keep in sync with `AccountServiceConnectionStatus` in `server/prisma/main/schema.prisma`.
 */
export enum ServiceConnectionStatus {
  notConnected = 'notConnected',
  profileOnly = 'profileOnly',
  requested = 'requested',
  pending = 'pending',
  connected = 'connected',
  /** The stored session stopped working; the artist must reconnect. */
  expired = 'expired',
}

/**
 * Where a "Possibly yours" suggestion came from.
 *
 * `catalog` is a name match against the public Music24 catalog — a guess, and
 * it carries a confidence. `profile` is a release read off the artist's own
 * public profile page, so it is as close to certain as this feature gets.
 *
 * Keep in sync with `CatalogSuggestion` in `server/src/app/components/Catalog`.
 */
export enum CatalogSuggestionSource {
  catalog = 'catalog',
  profile = 'profile',
}

/**
 * How sure the server is that a "Possibly yours" suggestion is a song that the catalog holds
 * already (`matchesCatalogItem` on the suggestion).
 *
 * `sure`: the same title without a store suffix (" - Single", " - EP"), the same artist and a
 * release date at most 14 days from the date of the row. `possible`: a title that only overlaps,
 * or the same title with no date or a date out of that window. The client offers "Merge" for both,
 * and "Not the same" lets the artist add the release as a new song.
 */
/**
 * The prefix of the dismissal that "Not the same" writes for a suggestion (`notSame:<catalogTrackMaid>`):
 * the suggestion stays, with no `matchesCatalogItem`, and shows as a plain new release.
 */
export const notSameCatalogDismissalPrefix = 'notSame:';

export enum CatalogSuggestionMatchConfidence {
  sure = 'sure',
  possible = 'possible',
}

/**
 * How the daily scrape of one artist profile is going.
 *
 * `unsupported` means we can read the url but not the service behind it, and it
 * is a settled state — nothing will retry it. `failing` means three scrapes in
 * a row failed, and the next one may still succeed.
 *
 * Keep in sync with `ArtistScrapeStatus` in `musicapi/prisma/main/schema.prisma`.
 */
export enum ArtistScrapeStatus {
  active = 'active',
  unsupported = 'unsupported',
  failing = 'failing',
  removed = 'removed',
}

/**
 * The key a release read off an artist profile is claimed and dismissed under.
 * No musicapi track sits behind it.
 */
export const profileCatalogMaidPrefix = 'profile:';

/**
 * The key a release the artist typed in by hand is stored under.
 *
 * Our catalog does not know the release — that is the whole reason the artist
 * is typing it — so the row needs a key of its own. It is a uuid rather than
 * anything derived from the release, because two artists adding the same single
 * are two independent rows and nothing may join them.
 */
export const manualCatalogMaidPrefix = 'manual:';

/**
 * Whether this key is one of ours rather than a musicapi track id.
 *
 * Everything that reads a track out of musicapi by maid has to ask this first.
 * A synthetic key would find nothing there, and the caller would report an
 * error for a row that is perfectly good.
 */
export function isSyntheticCatalogTrackMaid(catalogTrackMaid: string | null | undefined): boolean {
  if (catalogTrackMaid === null || catalogTrackMaid === undefined) {
    return false;
  }
  return catalogTrackMaid.startsWith(profileCatalogMaidPrefix) || catalogTrackMaid.startsWith(manualCatalogMaidPrefix);
}
